import { getScrollDirection } from "@/utils/scrollDirection";
import React, { useEffect, useRef, useState } from "react";

import { useRecommendedPageData } from "../";
import { useDashboardContext } from "../..";
import FilterTab from "../../../../assets/FilterTab";
import FilterLoader from "../../filterLoader/filterLoader";
import { convertCountInThousands } from "../../utils/convertCountInThousands";
import { normalizeToLowercase } from "../../utils/getStartCase";
import {
  RecommendedPageContextProps,
  RecommendedPageDataType,
  SelectedFilter,
} from "../types";
import "./index.css";

interface DashboardPropsType {
  isMobile: boolean;
}

const Filters = () => {
  const {
    recommendedJobData,
    setRecommendedJobData,
    handleEventTracking,
    cleverTapPropertyValues,
  }: RecommendedPageContextProps = useRecommendedPageData();

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const filters =
    Object.keys(recommendedJobData.data)
      .filter((data: string, index: number) => {
        return recommendedJobData?.data?.[data]?.data?.length > 0;
      })
      .map((data: string, index: number) => {
        return {
          id: data,
          selected:
            recommendedJobData?.selectedFilter?.length > 0
              ? data == recommendedJobData.selectedFilter[0].id
              : false,
          text:
            data && typeof data == "string" && data === "preference"
              ? `Based on ${normalizeToLowercase(data, "plural")}`
              : `Based on ${normalizeToLowercase(data)}`,
          count:
            recommendedJobData.data[data]?.meta?.paging?.total ||
            recommendedJobData.data[data]?.data?.length ||
            0,
        };
      }) || [];

  const [showFilter, setShowFilter] = useState(true);

  const handleFilter = (
    selectedOption: SelectedFilter[] | undefined,
    type: string,
  ) => {
    setRecommendedJobData((prev: RecommendedPageDataType) => {
      let newData = {
        ...prev,
        selectedFilter:
          selectedOption && type == "select"
            ? [...selectedOption]
            : type !== "remove"
              ? [...(prev?.selectedFilter || [])]
              : [],
      };
      if (!newData.stopFilterCalls) {
        newData.selectFilterFetch = "client";
        newData.filterDisable = true;
        newData.isLoadingJobDesc = true;
      }
      return newData;
    });
    handleEventTracking({
      eventName: "Pageviewed",
      eventType: "eventPush",
      eventProps: {
        page_name: `${cleverTapPropertyValues[recommendedJobData.activeTab as keyof typeof cleverTapPropertyValues]}${type == "remove" ? "" : selectedOption?.[0]?.text ? ` ${selectedOption[0].text}` : ""}`,
      },
    });
  };

  const handleMultiSelectCheckbox = (
    e: React.ChangeEvent<HTMLInputElement>,
    data: SelectedFilter,
  ) => {
    if (
      recommendedJobData.multiSelectCount > 0 &&
      !recommendedJobData.nextFilter &&
      !recommendedJobData.stopFilterCalls
    ) {
      setRecommendedJobData((prev: RecommendedPageDataType) => ({
        ...prev,
        discardBulkApplyPopup: {
          value: true,
          type: "filterSelect",
          decision: null,
          argumentStorage: [
            {
              ...data,
              selected: e.target.checked,
            },
          ],
        },
      }));
    } else {
      if (
        !recommendedJobData.stopFilterCalls &&
        !recommendedJobData.nextFilter
      ) {
        setRecommendedJobData((prev: RecommendedPageDataType) => ({
          ...prev,
          discardBulkApplyPopup: undefined,
          allMultiSelectSelected: false,
          clearAllMultiSelectSelected: true,
        }));
      } else if (
        recommendedJobData.stopFilterCalls &&
        !recommendedJobData.nextFilter
      ) {
        setRecommendedJobData((prev: RecommendedPageDataType) => ({
          ...prev,
          noScroll: false,
        }));
      }

      if (e.target.checked) {
        handleFilter([{ ...data, selected: e?.target?.checked }], "select");
      } else {
        handleFilter(undefined, "remove");
      }
    }
  };

  const handleFilterLabelChange = (
    e: React.KeyboardEvent<HTMLLabelElement>,
    index: number,
  ) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const dir = e.key === "ArrowDown" ? 1 : -1;
      const next = (index + dir + filters.length) % filters.length;
      inputRefs.current[next]?.focus();
      inputRefs.current[next]?.click();
    }
  };

  const fetchFilterOptions = (filterType: string) => {
    if (filterType === null || filterType === undefined || filterType === "")
      return;

    switch (filterType) {
      case "Job based on":
        return (
          <div className="relative w-full">
            <div
              role="tablist"
              aria-label="Recommended jobs filter"
              className="flex flex-col gap-3"
            >
              {filters?.slice(0, 5).map((data: SelectedFilter, idx: number) => {
                const inputId = `jobBasedOn-${data.id}`;
                const disabled = !!recommendedJobData.filterDisable;

                return (
                  <div key={`${data.id}-${data.count}`} className="relative">
                    {!!data.selected ? (
                      <div className="absolute top-[1px]">
                        <FilterTab width={4} height={34} />
                      </div>
                    ) : null}
                    <input
                      ref={(el) => {
                        inputRefs.current[idx] = el;
                      }}
                      id={inputId}
                      type="radio"
                      name="jobBasedOn"
                      checked={!!data.selected}
                      onChange={(e) => handleMultiSelectCheckbox(e, data)}
                      disabled={disabled}
                      className="sr-only"
                    />
                    <label
                      role="tab"
                      aria-selected={!!data.selected}
                      aria-controls={`${inputId}-panel`}
                      htmlFor={inputId}
                      tabIndex={-1}
                      onKeyDown={(e) => handleFilterLabelChange(e, idx)}
                      className={[
                        "group relative flex w-full items-center gap-2",
                        "py-2 pl-3 pr-3",
                        "transition-[background-color,color] duration-150",
                        "pointer-events-none",
                        disabled
                          ? "cursor-not-allowed opacity-60"
                          : "cursor-default",
                        !!data.selected
                          ? "text-center text-[14px] font-semibold leading-5 text-[#17142A]"
                          : "text-center text-[14px] font-normal leading-5 text-[#17142A]",
                      ].join(" ")}
                    >
                      <span className="whitespace-nowrap">
                        {data.text}
                        {data.count ? (
                          <span className="ml-1 text-[#6b7280]">
                            ({convertCountInThousands(data.count)})
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const { isMobile }: DashboardPropsType = useDashboardContext();

  useEffect(() => {
    if (recommendedJobData?.discardBulkApplyPopup?.decision) {
      if (recommendedJobData.discardBulkApplyPopup?.type == "filterSelect") {
        handleFilter(
          recommendedJobData.discardBulkApplyPopup?.argumentStorage,
          "select",
        );
      } else if (
        recommendedJobData.discardBulkApplyPopup?.type == "filterRemove"
      ) {
        handleFilter(undefined, "remove");
      }
      setRecommendedJobData((prev: RecommendedPageDataType) => {
        let newData = {
          ...prev,
          triggeredActionSource: "clearSelection",
        };
        return newData;
      });
    }
  }, [recommendedJobData?.discardBulkApplyPopup?.decision]);

  useEffect(() => {
    const handleScroll = () => {
      const direction = getScrollDirection();
      if (direction === "down") {
        setShowFilter(false);
      } else if (direction === "up") {
        setShowFilter(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (recommendedJobData.nextFilter) {
      if (inputRefs.current.length > 0) {
        const lastInput = inputRefs.current[inputRefs.current.length - 1];
        lastInput?.click();
      }
    }
  }, [recommendedJobData.nextFilter]);

  useEffect(() => {
    if (!recommendedJobData?.stopFilterCalls) return;
    if (!Array.isArray(recommendedJobData?.displayRecommendedData)) return;

    const container = document.getElementById("recommended-jobs-container");
    if (!container) return;

    const cutoff = Array.isArray(recommendedJobData?.data?.["preference"]?.data)
      ? recommendedJobData.data["preference"].data.length
      : 0;

    if ((inputRefs.current?.length || 0) < 2) return;

    const selectionRef = { current: null as "first" | "second" | null };

    const isElInContainerView = (el: HTMLElement) => {
      const cRect = container.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      return r.bottom > cRect.top && r.top + 100 < cRect.bottom;
    };

    const decide = () => {
      let anyVisible = false;
      let anyOutOfRange = false;

      recommendedJobData.displayRecommendedData.forEach(
        (_: any, idx: number) => {
          const el = document.getElementById(
            `job-card-${idx}`,
          ) as HTMLElement | null;
          if (!el) return;

          if (isElInContainerView(el)) {
            anyVisible = true;
            if (idx >= cutoff) anyOutOfRange = true;
          }
        },
      );

      if (!anyVisible) return;

      if (anyOutOfRange) {
        if (selectionRef.current !== "second") {
          const second = inputRefs.current[1];
          if (second && !second.checked) {
            second.click();
            selectionRef.current = "second";
          }
        }
      } else {
        if (selectionRef.current !== "first") {
          const first = inputRefs.current[0];
          if (first && !first.checked) {
            first.click();
            selectionRef.current = "first";
          }
        }
      }
    };

    decide();

    container.addEventListener("scroll", decide, { passive: true });

    window.addEventListener("resize", decide);

    return () => {
      container.removeEventListener("scroll", decide);
      window.removeEventListener("resize", decide);
    };
  }, [
    recommendedJobData?.stopFilterCalls,
    recommendedJobData?.displayRecommendedData,
    recommendedJobData?.data?.["preference"]?.data,
    isMobile,
  ]);

  return (
    <>
      {recommendedJobData.isLoadingJobDesc &&
      !recommendedJobData.stopFilterCalls &&
      !recommendedJobData.nextFilter &&
      !isMobile ? (
        <FilterLoader isMobile={isMobile} />
      ) : (
        <div className="hidden">
          {recommendedJobData.filterStatusPresent && (
            <div
              className={`border-1 relative flex w-full flex-col ${isMobile ? "gap-2" : "gap-4"} border-jobCardBorder rounded-2xl border pb-3 pl-3 pr-3 pt-6`}
            >
              <div className="flex flex-col px-3">
                <span className="text-base font-bold not-italic leading-6 text-[#1D1934]">
                  Recommended Jobs
                  <hr className="text-surface-primary-normal mt-3 text-xs" />
                </span>
              </div>
              <div className="no-scrollbar relative flex flex-col overflow-auto pt-2">
                {["Job based on"].map((filterType) => {
                  return fetchFilterOptions(filterType);
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default React.memo(Filters);
